import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ClipboardList, RefreshCw, History } from "lucide-react";
import { toast } from "sonner";

const ENTITY_LABEL = {
  hero_slides: "Hero Slide",
  projects: "Project",
  leaders: "Leader",
  blogs: "Blog",
  careers: "Career",
  leads: "Lead",
};

const ACTION_COLOR = {
  create: "bg-emerald-50 text-emerald-700",
  update: "bg-blue-50 text-blue-700",
  delete: "bg-rose-50 text-rose-700",
  restore: "bg-amber-50 text-amber-700",
  update_status: "bg-slate-100 text-slate-700",
};

export default function AdminAuditLog() {
  const [items, setItems] = useState([]);
  const [versionsView, setVersionsView] = useState(null); // { entity, entity_id, items }

  const load = async () => {
    try {
      const { data } = await api.get("/admin/audit-log");
      setItems(data);
    } catch {
      toast.error("Could not load audit log.");
    }
  };

  useEffect(() => { load(); }, []);

  const openVersions = async (entity, entity_id) => {
    try {
      const { data } = await api.get(`/admin/versions/${entity}/${entity_id}`);
      setVersionsView({ entity, entity_id, items: data });
    } catch {
      toast.error("Could not load versions.");
    }
  };

  const restoreVersion = async (versionId) => {
    if (!window.confirm("Restore this version? Current content will be saved as a new version first.")) return;
    try {
      await api.post(`/admin/versions/${versionId}/restore`);
      toast.success("Version restored.");
      setVersionsView(null);
      load();
    } catch {
      toast.error("Restore failed.");
    }
  };

  return (
    <div data-testid="admin-audit-log" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="section-overline">Governance</div>
          <h1 className="font-display text-3xl font-semibold text-[#0A1128] mt-2 flex items-center gap-3">
            <ClipboardList size={24} className="text-[#FF8C00]" /> Audit Log
          </h1>
          <p className="mt-2 text-slate-600">Every change to published content is tracked here. Open an entry to view its version history and roll back.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold hover:border-[#FF8C00]">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {items.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No activity yet.</div>}
        {items.map((a) => (
          <div key={a.id} className="p-5 border-b border-slate-100 last:border-b-0 flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className={`px-2.5 py-0.5 rounded-md text-xs font-semibold uppercase tracking-wider ${ACTION_COLOR[a.action] || "bg-slate-100 text-slate-700"}`}>
                  {a.action}
                </span>
                <span className="font-semibold text-[#0A1128]">{ENTITY_LABEL[a.entity] || a.entity}</span>
                {a.entity_id && <code className="text-xs text-slate-500 font-mono-display">{a.entity_id.slice(-8)}</code>}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                by <span className="font-semibold">{a.actor_email}</span> · {a.at}
              </div>
            </div>
            {a.entity_id && a.entity !== "leads" && (
              <button onClick={() => openVersions(a.entity, a.entity_id)} className="inline-flex items-center gap-2 rounded-md border border-slate-200 hover:border-[#FF8C00] px-3 py-2 text-xs font-semibold">
                <History size={14} /> Versions
              </button>
            )}
          </div>
        ))}
      </div>

      {versionsView && (
        <div className="fixed inset-0 z-50 bg-[#050914]/70 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <div className="section-overline">Version history</div>
                <h2 className="font-display text-2xl font-semibold text-[#0A1128] mt-2">
                  {ENTITY_LABEL[versionsView.entity]} · {versionsView.entity_id.slice(-8)}
                </h2>
              </div>
              <button onClick={() => setVersionsView(null)} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold">Close</button>
            </div>
            <div>
              {versionsView.items.length === 0 && <div className="p-6 text-sm text-slate-500">No prior versions yet — this item has only been created.</div>}
              {versionsView.items.map((v) => (
                <div key={v.id} className="p-5 border-b border-slate-100 last:border-b-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-[#0A1128]">v{v.version}</div>
                      <div className="text-xs text-slate-500">{v.saved_at} · by {v.saved_by}</div>
                    </div>
                    <button onClick={() => restoreVersion(v.id)} className="inline-flex items-center gap-2 rounded-md bg-[#0A1128] hover:bg-[#15244B] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wide">
                      <RefreshCw size={12} /> Restore
                    </button>
                  </div>
                  <div className="mt-3 text-sm text-slate-700 line-clamp-3">
                    <span className="text-slate-500">Title:</span>{" "}
                    {v.snapshot?.title || v.snapshot?.name || "—"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
